/**
 * ContentRefreshPreviewSlot
 *
 * Renders a live-polling task queue table for the content refresh agent
 * inside a chat behaviour slot. Fetches data from key-val store buckets
 * and displays a read-only table with status updates.
 *
 * Action buttons navigate to the full agent page rather than opening
 * modals inline, keeping the slot as a lightweight preview.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { Button } from '@strivelabs/ui-core';
import { LoadingSkeleton } from '@strivelabs/ui-blocks';
import { z } from 'zod';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { useContentRefreshData } from '@/hooks/useContentRefreshData';
import { ROUTES } from '@/agents/blog-post-content-refresher/constants';
import { ContentRefreshTable } from '@/components/content-refresh/ContentRefreshTable';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import { useStoreAndFetch } from '@/hooks/useStoreAndFetch';
import { getBucketConfig } from '@/constants/contentRefreshBucketRegistry';
import { useResolveWorkflowInstanceId } from '@/hooks/useResolveWorkflowInstanceId';
import { usePersistentEntryStore } from '@/hooks/usePersistentEntryStore';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import {
  FullscreenSlotOverlay,
  SlotContainer,
  parseKeywords,
  getSlotActionButton,
} from './shared';

/** Zod schema for Content Refresh entry validation */
const ContentRefreshEntrySchema = z.object({
  blog_post_url: z.string().optional(),
  primary_keywords: z.union([z.array(z.string()), z.string()]).optional(),
});

export interface ContentRefreshPreviewSlotProps {
  action: BehaviourAgentExperienceAction;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
}

export function ContentRefreshPreviewSlot({
  action,
  chatHistoryId,
  behaviourActionState,
}: ContentRefreshPreviewSlotProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const agentShortName = action.agent_short_name;
  const resolvedWorkflowInstanceId = useResolveWorkflowInstanceId(agentShortName);
  const workflowInstanceId = resolvedWorkflowInstanceId ?? '';
  const entries = action.data?.entries;

  const { workflowInstanceId: ctxWorkflowInstanceId, workflowExecutionId } = useChatMetadata();

  // Use hook to manage persistent entry storage state
  const { alreadyStored, handleStoreSuccess } = usePersistentEntryStore(
    action,
    behaviourActionState,
    chatHistoryId,
    ctxWorkflowInstanceId,
    workflowExecutionId
  );

  // Get bucket configuration for this agent
  const bucketConfig = getBucketConfig(agentShortName);

  // Transform entry from backend format to store format
  const transformEntry = useCallback((entry: unknown) => {
    const parsed = ContentRefreshEntrySchema.safeParse(entry);
    if (!parsed.success) {
      // Validation failed - return default values
      console.warn('[ContentRefreshPreviewSlot] Invalid entry format:', parsed.error);
      return {
        blog_post_url: '',
        primary_keywords: [],
      };
    }
    const e = parsed.data;
    return {
      blog_post_url: e.blog_post_url || '',
      primary_keywords: parseKeywords(e.primary_keywords),
    };
  }, []);

  // Store entries first (if provided), then enable fetching
  // If entries were already stored (from behaviour_action_state), skip store phase
  const { shouldFetch, isStoring, storeError } = useStoreAndFetch({
    entries,
    workflowInstanceId,
    bucketName: bucketConfig?.todoBucket ?? '',
    appName: bucketConfig?.appName ?? '',
    agentShortName,
    transformEntry,
    alreadyStored,
    onStoreSuccess: handleStoreSuccess,
  });

  const { data, isLoading, isError } = useContentRefreshData({
    workflowInstanceId,
    agentShortName,
    enabled: !!workflowInstanceId,
    shouldFetch,
  });

  const handleNavigateToAgent = useCallback(() => {
    navigate(ROUTES.BASE);
  }, [navigate]);

  const renderAction = useCallback(
    (row: { _status: ProcessingStatus; data?: { workflow_execution_id?: string }; key?: string }) => {
      // For completed rows, navigate directly to the result page
      if (row._status === ProcessingStatus.COMPLETED && row.data?.workflow_execution_id && row.key) {
        const resultUrl = `${ROUTES.result(row.data.workflow_execution_id)}?key=${row.key}`;
        return getSlotActionButton({
          status: row._status,
          onNavigateToAgent: () => navigate(resultUrl),
        });
      }
      // For waiting-for-approval rows, navigate directly to the review page
      if (row._status === ProcessingStatus.WAITING_FOR_APPROVAL && row.data?.workflow_execution_id && row.key) {
        const reviewUrl = `${ROUTES.review(row.data.workflow_execution_id)}?key=${row.key}`;
        return getSlotActionButton({
          status: row._status,
          onNavigateToAgent: () => navigate(reviewUrl),
        });
      }
      return getSlotActionButton({
        status: row._status,
        onNavigateToAgent: handleNavigateToAgent,
      });
    },
    [handleNavigateToAgent, navigate]
  );

  // Resolve the chat container on mount
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  if (!workflowInstanceId) return null;

  // Store phase loading state
  if (isStoring) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
          <p className="text-sm text-muted-foreground mt-2">
            Adding entries to task queue...
          </p>
        </div>
      </SlotContainer>
    );
  }

  // Fetch phase loading state
  if (isLoading) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      </SlotContainer>
    );
  }

  // Error or empty state
  if (isError || data.length === 0) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <p className="text-sm text-muted-foreground">
            {isError ? 'Unable to load content refresh tasks.' : 'No content refresh tasks found.'}
          </p>
        </div>
      </SlotContainer>
    );
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <Button
          variant="overlay"
          size="iconSm"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </Button>

        {/* Store error warning */}
        {storeError && (
          <div className="mx-6 mt-6 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              Failed to add new entries. Showing existing entries only.
            </p>
          </div>
        )}

        {/* Table content */}
        <div className={storeError ? "px-6 pb-6" : "p-8"}>
          <ContentRefreshTable data={data} renderAction={renderAction} />
        </div>
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            <ContentRefreshTable data={data} renderAction={renderAction} />
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>
    </>
  );
}
